import {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import { useNavigate } from 'react-router-dom'; 
import { IoIosAddCircleOutline, IoMdTrash, IoMdCreate, IoMdInformationCircle, IoMdClose } from 'react-icons/io';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import styles from '../Universidades/TablaCrud.module.css'; 
import Swal from 'sweetalert2';
import AniadirBeca from './AniadirBeca';
import {useAuth} from '../../Auth/AuthContext'; 

const Becas = () => { 
    const [becas, setBecas] = useState([]); 
    const [loading, setLoading] = useState(true); 
    const [error, setError] = useState(null); 
    const [searchTerm, setSearchTerm] = useState(''); 
    const navigate = useNavigate(); 
    const [currentPage, setCurrentPage] = useState(1); 
    const becasPorPagina = 10; 
    const abortControllerRef = useRef(null);
    const {getToken} = useAuth(); 
    const token = getToken(); 
    
    const [modalAbierto, setModalAbierto] = useState(false);
    const [becaAEditar, setBecaAEditar] = useState(null);
    
    // Estado para el modal de información
    const [modalInfoAbierto, setModalInfoAbierto] = useState(false);
    const [infoSeleccionada, setInfoSeleccionada] = useState({ titulo: '', contenido: '' });

    const tiposBecas = {
        1: 'Estatal',
        2: 'Federal', 
        3: 'Municipal',
        4: 'Escolar',
        5: 'Otra'
    };

    const fetchBecas = useCallback(async () => { 
        try { 
            setLoading(true); 
            setError(null); 
            if (!token) { 
                setError('No se encontró token de autenticación.');
                navigate('/signin'); 
                return; 
            }
            if (abortControllerRef.current) { 
                abortControllerRef.current.abort(); 
            }
            abortControllerRef.current = new AbortController(); 

            const url = `${process.env.REACT_APP_API_URL}/universidades/becas`

            const response = await fetch(url, { 
                method: 'GET', 
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }, 
                signal: abortControllerRef.current.signal
            }); 

            if (!response.ok) { 
                throw new Error(`HTTP error :(: ${response.status}`);
            } 
                const data = await response.json(); 
                setBecas(data); 
            } catch (err) { 
                if (err.name !== 'AbortError') { 
                    console.error('Error al obtener becas. ', err); 
                    setError('Error al cargar las becas. Por favor, intenta de nuevo más tarde. '); 
                }
            } finally { 
                setLoading(false); 
            }
        }, [navigate]); 

        useEffect(() => { 
            fetchBecas(); 
            return () => { 
                if (abortControllerRef.current) { 
                    abortControllerRef.current.abort(); 
                }
            }; 
        }, [fetchBecas]); 

        const becasFiltradas = useMemo(() => { 
            const searchTermLower = searchTerm.toLowerCase(); 
            return becas.filter(beca => 
                beca.nombre?.toLowerCase().includes(searchTermLower) ||
                beca.info?.toLowerCase().includes(searchTermLower) ||
                tiposBecas[beca.tipo]?.toLowerCase().includes(searchTermLower)
            );
        }, [becas, searchTerm]);

        const paginationData = useMemo(() => { 
            const totalPages = Math.ceil(becasFiltradas.length / becasPorPagina); 
            const indexOfLastBeca = currentPage * becasPorPagina; 
            const indexOfFirstBeca = indexOfLastBeca - becasPorPagina; 
            const becasActuales = becasFiltradas.slice(indexOfFirstBeca, indexOfLastBeca); 

            return { 
                totalPages, 
                indexOfFirstBeca, 
                indexOfLastBeca, 
                becasActuales
            }; 
        }, [becasFiltradas, currentPage, becasPorPagina]); 

        const handleSearchChange = useCallback((e) => { 
            setSearchTerm(e.target.value); 
            setCurrentPage(1); 
        }, []); 

        const paginate = useCallback((pageNumber) => {
            setCurrentPage(pageNumber); 
        }, []);

        const abrirModalAgregar = () => {
            setBecaAEditar(null);
            setModalAbierto(true);
        };

        const abrirModalEditar = (beca) => {
            setBecaAEditar(beca);
            setModalAbierto(true);
        };

        const cerrarModal = () => {
            setModalAbierto(false);
            setBecaAEditar(null);
        };

        const abrirModalInfo = (titulo, contenido) => {
            setInfoSeleccionada({ titulo, contenido });
            setModalInfoAbierto(true);
        };

        const cerrarModalInfo = () => {
            setModalInfoAbierto(false);
            setInfoSeleccionada({ titulo: '', contenido: '' });
        };

        const handleBecaGuardada = () => {
            fetchBecas(); 
        };

        const handleDelete = useCallback(async (id) => {
            const result = await Swal.fire({
                title: '¿Estás seguro?',
                text: "Esta acción no se puede deshacer",
                icon: 'warning',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });

            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/eliminar-beca/${id}`, {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.ok) {
                        setBecas(becas.filter(beca => beca.id !== id));
                        Swal.fire({
                            title: 'Beca actualizada correctamente.',
                            icon: 'success',
                            confirmButtonText: 'Continuar',
                            confirmButtonColor: '#242b57',
                            customClass: { popup: 'popupbonito' }
                            });
                    } else {
                        throw new Error('Error al eliminar la beca');
                    }
                } catch (error) {
                    console.error('Error:', error);
                    Swal.fire('Error', 'No se pudo eliminar la beca', 'error');
                }
            }
        }, [becas]);

        if (loading) {
            return (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando becas...</p>
                    </div>
                </div>
            );
        }
        
    if (error) {
        return (
            <div className={styles.errorAlert} role="alert">
                <strong>Error: </strong>
                <span>{error}</span>
            </div>
        );
    }


    return ( 
        <div className={styles.tablaContainer}>
            <h2 className={styles.tablaTitulo}>Gestión de Becas</h2>

            <div className={styles.searchExportContainer}>
                <div className={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder="Buscar becas..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                    />
                </div>

                <div className={styles.buttonContainer}>
                    <button 
                        className={styles.botonAdd}
                        onClick={abrirModalAgregar}
                    >
                        <IoIosAddCircleOutline /> Agregar Beca
                    </button>
                </div>
            </div>

            <div className={styles.tablaOverflow}>
                <table className={styles.tablaUsuarios}>
                    <thead className={styles.tablaHeader}>
                        <tr>
                            <th className={styles.tablaCelda}>Nombre</th>
                            <th className={styles.tablaCelda}>Tipo</th>
                            <th className={styles.tablaCelda}>Información</th>
                            <th className={styles.tablaCelda}>Link</th>
                            <th className={styles.tablaCelda}>Fecha Inicial</th>
                            <th className={styles.tablaCelda}>Fecha Final</th>
                            <th className={styles.tablaCelda}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginationData.becasActuales.length > 0 ? (
                            paginationData.becasActuales.map((beca) => (
                                <tr key={beca.id} className={styles.tablaFila}>
                                    <td className={styles.tablaCelda}>{beca.nombre}</td>
                                    <td className={styles.tablaCelda}>{tiposBecas[beca.tipo] || 'Sin tipo'}</td>
                                    <td className={styles.tablaCelda}>
                                        <div className={styles.accionesContainer}>
                                        <button
                                            className={`${styles.botonAccion} ${styles.editar}`}
                                            onClick={() => abrirModalInfo(beca.nombre, beca.info)}
                                            title="Ver información completa"
                                        >
                                            <IoMdInformationCircle />
                                        </button>
                                        </div>
                                    </td>
                                    <td className={styles.tablaCelda}>
                                        {beca.link ? (
                                            <a 
                                                href={beca.link} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                            >
                                                {beca.link}
                                            </a>
                                        ) : (
                                            'Sin enlace'
                                        )}
                                    </td>
                                    <td className={styles.tablaCelda}>
                                        {beca.fecha_inicio ? new Date(beca.fecha_inicio).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className={styles.tablaCelda}>
                                        {beca.fecha_final ? new Date(beca.fecha_final).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className={styles.tablaCelda}>
                                        <div className={styles.accionesContainer}>
                                            <button 
                                                className={`${styles.botonAccion} ${styles.editar}`}
                                                onClick={() => abrirModalEditar(beca)}
                                                title="Editar beca"
                                            >
                                                <IoMdCreate />
                                            </button>
                                            <button 
                                                className={`${styles.botonAccion} ${styles.eliminar}`}
                                                onClick={() => handleDelete(beca.id)}
                                                title="Eliminar beca"
                                            >
                                                <IoMdTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className={styles.tablaNoResultados}>
                                    {searchTerm && !loading ? 
                                        'No se encontraron becas que coincidan con la búsqueda' : 
                                        'No hay becas disponibles'
                                    }
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {becasFiltradas.length > 0 && paginationData.totalPages > 1 && (
                <div className={styles.paginacionContainer}>
                    <div>
                        <p className={styles.paginacionInfo}>
                            Mostrando {paginationData.indexOfFirstBeca + 1} - {
                                Math.min(paginationData.indexOfLastBeca, becasFiltradas.length)
                            } de {becasFiltradas.length} becas
                        </p>
                    </div>
                    <div className={styles.paginacionControles}>
                        <button 
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`${styles.paginacionBoton} ${currentPage === 1 ? styles.deshabilitado : styles.primario}`}
                            aria-label="Página anterior"
                        >
                            <FaArrowLeft />
                        </button>
                        <select
                            className={styles.paginacionSelect}
                            value={currentPage}
                            onChange={(e) => paginate(Number(e.target.value))}
                            aria-label="Seleccionar página"
                        >
                            {Array.from({ length: paginationData.totalPages }).map((_, index) => (
                                <option key={index} value={index + 1}>
                                    {index + 1}
                                </option>
                            ))}
                        </select>
                        <button 
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === paginationData.totalPages}
                            className={`${styles.paginacionBoton} ${
                                currentPage === paginationData.totalPages ? styles.deshabilitado : styles.primario
                            }`}
                            aria-label="Página siguiente"
                        >
                            <FaArrowRight />
                        </button>
                    </div>
                </div>
            )}

            {/* Modal para agregar/editar beca */}
            <AniadirBeca
                abrirModal={modalAbierto}
                cerrarModal={cerrarModal}
                becaAEditar={becaAEditar}
                onBecaGuardada={handleBecaGuardada}
            />

            {/* Modal para mostrar información completa */}
            {modalInfoAbierto && (
                <div className={styles.modalOverlay} onClick={cerrarModalInfo}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Información de la Beca</h3>
                            <button 
                                className={styles.modalCloseButton}
                                onClick={cerrarModalInfo}
                                title="Cerrar"
                            >
                                <IoMdClose />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <h4 className={styles.infoTitle}>{infoSeleccionada.titulo}</h4>
                            <div className={styles.infoContent}>
                                {infoSeleccionada.contenido}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}; 

export default Becas;